import React, { useCallback, useEffect, useMemo, useState } from 'react';
import stylesEditor from '../../styles/CreativeEditor.module.css';
import { useAtlasStore, AtlasState } from '../../../../store/atlasStore';
import { atlasClient } from '../../../../api/atlasClient';
import ReactMarkdown from 'react-markdown';
import stylesCompare from '../../styles/CompareItemsModal.module.css';
import { useCitation } from '../../../../hooks/useCitation';
import { normalizeMarkdownBullets } from '../../utils/markdown';

interface CompareItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemFqns: string[];
}

type LibraryItem = any;

export const CompareItemsModal: React.FC<CompareItemsModalProps> = ({ isOpen, onClose, itemFqns }) => {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const creativeTypes = useAtlasStore((s: AtlasState) => s.sparkCreativeTypes);
  const storeDimensions = useAtlasStore((s: AtlasState) => s.dimensions);
  const dimensions = useMemo(() => storeDimensions || [], [storeDimensions]);

  // Map element fqn -> display name for tag chips
  const elementNameMap = useMemo(() => {
    const map = new Map<string, string>();
    dimensions.forEach(d => (d.elements || []).forEach((e: any) => { if (e?.fqn && e?.name) map.set(e.fqn, e.name); }));
    return map;
  }, [dimensions]);

  // Markdown rendering components/plugins (same as item mode: no project citations, strip refs)
  const { markdownComponents, markdownPlugins } = useCitation({ projectFqn: undefined, stripBracketRefs: true });

  // Derive creative type label from selected items (lowercased plural when available)
  const typeLabelLower = useMemo(() => {
    try {
      const typeFqns = Array.from(new Set(items.map((it: any) => String(it?.creative_type_fqn || ''))).values()).filter(Boolean);
      if (typeFqns.length === 1) {
        const ct = (creativeTypes || []).find((t: any) => String(t.fqn) === typeFqns[0]);
        const label = ct?.plural_name || ct?.name || 'Items';
        return String(label).toLowerCase();
      }
    } catch (_) { /* ignore */ }
    return 'items';
  }, [items, creativeTypes]);

  // Close on ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(itemFqns.map(fqn => atlasClient.getLibraryItem(fqn)));
        if (!cancelled) setItems(results);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load items');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, itemFqns]);

  // Compute hero image URL for each item
  const heroUrls = useMemo(() => {
    return items.map(item => {
      const ct = (creativeTypes || []).find(ct => String(ct.fqn) === String(item.creative_type_fqn));
      const images = (item as any).images || {};
      const heroId = (ct as any)?.hero_image_element_id;
      let fileId: string | null = null;
      if (heroId && images[heroId] && Array.isArray(images[heroId].current_file_ids) && images[heroId].current_file_ids.length > 0) {
        fileId = images[heroId].current_file_ids[0];
      } else {
        for (const key of Object.keys(images)) {
          const slot = images[key];
          const current = slot?.current_file_ids;
          if (Array.isArray(current) && current.length > 0) { fileId = current[0]; break; }
        }
      }
      return fileId ? atlasClient.getAssetUrl(fileId, { w: 400, h: 400 }) : null;
    });
  }, [items, creativeTypes]);

  // Build a union of element ids across all items for side-by-side rows
  const elementDefsByItem = useMemo(() => items.map(item => (item?.elements_definition || {}) as Record<string, any>), [items]);
  const formsOverlayByItem = useMemo(() => items.map(item => (item?.forms_overlay || []) as { id: string; groups: { label: string; elements: string[] }[] }[]), [items]);

  // Prefer ordering by the first item's forms overlay; fallback to union sorted by label
  const orderedElementIds = useMemo(() => {
    const first = formsOverlayByItem[0] || [];
    const fromFirst: string[] = [];
    first.forEach(f => (f.groups || []).forEach(g => (g.elements || []).forEach(eid => fromFirst.push(eid))));
    const union = new Set<string>(fromFirst);
    elementDefsByItem.forEach(defs => { Object.keys(defs).forEach(id => union.add(id)); });
    // Keep fromFirst order, then append others sorted by id
    const remaining = Array.from(union).filter(id => !fromFirst.includes(id)).sort();
    return [...fromFirst, ...remaining];
  }, [formsOverlayByItem, elementDefsByItem]);

  // Suppress media fields (e.g., image elements) from the grid
  const nonMediaElementIds = useMemo(() => {
    return orderedElementIds.filter(id => {
      // If any item defines this element as an image, treat it as media and exclude
      return !elementDefsByItem.some(defs => (defs?.[id]?.type || '').toLowerCase() === 'image');
    });
  }, [orderedElementIds, elementDefsByItem]);

  const labelForElement = useCallback((id: string): string => {
    for (const defs of elementDefsByItem) {
      const el = defs[id];
      if (el?.label) return el.label;
    }
    return id;
  }, [elementDefsByItem]);

  const renderValue = (item: any, id: string): React.ReactNode => {
    const defs = (item?.elements_definition || {}) as Record<string, any>;
    const el = defs[id] || {};
    const values = (item?.values || {}) as Record<string, any>;
    const images = (item?.images || {}) as Record<string, any>;
    if ((el.type || '').toLowerCase() === 'image') {
      const fileIds: string[] = (images?.[id]?.current_file_ids || []) as string[];
      if (!fileIds || fileIds.length === 0) return <div className={stylesCompare.emptyCell}>---</div>;
      const first = fileIds[0];
      return (
        <div style={{ width: '100%', maxWidth: 400, aspectRatio: '1 / 1', margin: '0 auto', borderRadius: 6, overflow: 'hidden', background: '#f3f4f6' }}>
          <img
            src={atlasClient.getAssetUrl(first, { w: 400, h: 400 })}
            alt={labelForElement(id)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      );
    }
    const v = values[id];
    if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0)) {
      return <div className={stylesCompare.emptyCell}>---</div>;
    }
    // Option fields: map stored id(s) to labels using element definition
    const isOption = String(el?.type || '').toLowerCase() === 'option';
    if (isOption) {
      const options: Array<{ id: any; label: any }> = Array.isArray(el?.options) ? el.options : [];
      const idToLabel = new Map<string, string>(
        options.map((o) => [String(o?.id ?? '').toLowerCase(), String(o?.label ?? o?.id ?? '')])
      );
      const toLabel = (val: any): string => {
        if (val && typeof val === 'object') {
          const id = String((val as any).id ?? '').toLowerCase();
          return idToLabel.get(id) ?? String((val as any).label ?? id);
        }
        const id = String(val).toLowerCase();
        return idToLabel.get(id) ?? String(val);
      };
      if (Array.isArray(v)) {
        return v.length ? toLabel(v[0]) + (v.length > 1 ? ', ' + v.slice(1).map(toLabel).join(', ') : '') : <div className={stylesCompare.emptyCell}>---</div>;
      }
      return toLabel(v);
    }
    // If element is marked as markdown, render markdown
    const isMarkdown = Boolean(el?.markdown) && (el?.type === 'textarea' || el?.type === 'text');
    if (isMarkdown && typeof v === 'string') {
      return (
        <div style={{ fontSize: 13, lineHeight: 1.5 }} className={stylesCompare.cellMarkdown}>
          <ReactMarkdown components={markdownComponents} remarkPlugins={markdownPlugins}>
            {normalizeMarkdownBullets(v)}
          </ReactMarkdown>
        </div>
      );
    }
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
  };

  const tagNamesFor = (item: any): string[] => {
    const pattern = (item?.tag_pattern || []) as { element_fqns: string[] }[];
    const names: string[] = [];
    (pattern || []).forEach(entry => (entry.element_fqns || []).forEach(fqn => {
      const n = elementNameMap.get(fqn);
      if (n) names.push(n);
    }));
    return names;
  };

  // Determine if any item has tags to show; if none, omit the tags row entirely
  const hasAnyTags = useMemo(() => {
    return items.some(item => {
      const pattern = (item?.tag_pattern || []) as { element_fqns: string[] }[];
      return (pattern || []).some(entry => (entry.element_fqns || []).length > 0);
    });
  }, [items]);

  return (
    <div className={`${stylesEditor.modalOverlay} ${stylesEditor.open}`}>
      <div className={`${stylesEditor.modalContent} ${stylesEditor.open}`}>
            <div className={stylesEditor.modalHeader}>
              <div className={stylesEditor.modalHeaderContent}>
                <div className={stylesEditor.headerTitleRow}>
                  <button className={stylesEditor.headerBackButton} onClick={onClose} aria-label="Back">
                    <svg focusable="false" aria-hidden="true" viewBox="0 0 24 24" width="20" height="20">
                      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
                    </svg>
                  </button>
                  <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-color, #374046)' }}>{`Compare ${typeLabelLower}`}</h2>
                </div>
                {error && <div style={{ color: '#d32f2f' }}>{error}</div>}
              </div>
            </div>
        <div className={stylesEditor.modalBody}>
          {loading ? (
            <div className={stylesEditor.loading}>Loading…</div>
          ) : (
            // Make entire view scrollable
            <div style={{ height: '100%', overflow: 'auto' }}>
              <div className={stylesEditor.mainContentArea}>
                <div className={`${stylesEditor.mainContentInner} ${stylesEditor.mainContentInnerFull}`}>
                {/* Grid container: first column is labels, next columns are items */}
                <div className={stylesEditor.compareGrid} style={{ ['--compare-items' as any]: items.length }}>
                  {/* Row: Image (no label) */}
                  <div />
                  {heroUrls.map((url, idx) => (
                    <div key={`img-${idx}`}>
                      {url ? (
                        <div style={{ width: '100%', maxWidth: 400, aspectRatio: '1 / 1', margin: '0 auto', borderRadius: 6, overflow: 'hidden', background: '#f3f4f6' }}>
                          <img src={url} alt="Hero" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      ) : (
                        <div style={{ width: '100%', maxWidth: 400, aspectRatio: '1 / 1', margin: '0 auto', borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                          {/* Reserve square space even when missing */}
                          <span>—</span>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Row: Title (no label) */}
                  <div />
                  {items.map((item, idx) => (
                    <div key={`t-${idx}`} style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.3, wordBreak: 'break-word' }}>
                      {item.title || 'Untitled'}
                    </div>
                  ))}

                  {/* Row: Description (no label) */}
                  <div />
                  {items.map((item, idx) => (
                    <div key={`d-${idx}`} style={{ color: '#6b7280', fontSize: 13, lineHeight: 1.4, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {item.description ? item.description : <span style={{ color: '#999' }}>—</span>}
                    </div>
                  ))}

                  {/* Row: Tags (only if any item has tags) */}
                  {hasAnyTags && (
                    <>
                      <div />
                      {items.map((item, idx) => {
                        const names = tagNamesFor(item);
                        return (
                          <div key={`tags-${idx}`} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 12 }}>
                            {names.length === 0 ? (
                              <span style={{ color: '#999' }}>—</span>
                            ) : names.map((n, i) => (
                              <span key={`${n}-${i}`} style={{ background: '#E6F0FE', color: '#1D4ED8', padding: '2px 6px', borderRadius: 999, fontSize: 12 }}>{n}</span>
                            ))}
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Forms values */}
                  {nonMediaElementIds.map((id) => (
                    <React.Fragment key={`row-${id}`}>
                      <div style={{ fontWeight: 500, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.4, minWidth: 0 }}>{labelForElement(id)}</div>
                      {items.map((item, idx) => (
                        <div key={`cell-${id}-${idx}`} style={{ fontSize: 13, lineHeight: 1.5 }}>
                          {renderValue(item, id)}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompareItemsModal;
